import { useMemo, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import CONFIG from '@playcastdotio/GlobalConfig';
import { useActiveCampaigns } from '../../../hooks/useActiveCampaigns';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * GameGalleryGrid Component
 * Displays all active campaigns in a responsive grid.
 * Cards match the CampaignCarousel card style (gradient border, video, overlay).
 * Clicking a card opens the demo in a new browser tab.
 * Desktop: 4→3 column grid at 1024px design width
 * Mobile: Stubbed for future implementation
 */

type GalleryCard = {
  id: string;
  title: string;
  description: string;
  demoUrl: string;
  videoSrc?: string;
  posterSrc?: string;
};

const FALLBACK_POSTER = 'https://i.imgur.com/LwEz0bc.jpg';

const normalizeTheme = (theme: Record<string, any>) => {
  const nestedTheme = theme?.theme;
  if (nestedTheme && typeof nestedTheme === 'object') {
    const nestedRecord = nestedTheme as Record<string, any>;
    if (nestedRecord.theme && typeof nestedRecord.theme === 'object') {
      return nestedRecord.theme as Record<string, any>;
    }
    return nestedTheme as Record<string, any>;
  }
  return theme;
};

const isVideoUrl = (value?: string) => Boolean(value && /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(value));

const resolveThemeVideo = (themeInput: Record<string, any>) => {
  const theme = normalizeTheme(themeInput);
  const trailer = theme?.trailerBackgroundVideo;
  const loading = theme?.loadingBackgroundVideo;
  const trailerVideoUrl =
    typeof trailer === 'string' ? trailer : typeof trailer?.videoUrl === 'string' ? trailer.videoUrl : undefined;
  const loadingVideoUrl =
    typeof loading === 'string' ? loading : typeof loading?.videoUrl === 'string' ? loading.videoUrl : undefined;
  const trailerPoster =
    typeof trailer === 'object' && typeof trailer?.posterImage === 'string' ? trailer.posterImage : undefined;
  const loadingPoster =
    typeof loading === 'object' && typeof loading?.posterImage === 'string' ? loading.posterImage : undefined;
  const fallbackVideo =
    (isVideoUrl(theme?.playNowModalBackgroundImage) ? theme?.playNowModalBackgroundImage : undefined) ??
    (isVideoUrl(theme?.pageBackgroundImage) ? theme?.pageBackgroundImage : undefined);
  return {
    videoUrl: trailerVideoUrl ?? loadingVideoUrl ?? theme?.playNowModalBackgroundVideo ?? theme?.videoUrl ?? theme?.trailerVideoUrl ?? fallbackVideo,
    posterUrl:
      trailerPoster ??
      loadingPoster ??
      theme?.playNowModalBackgroundImage ??
      theme?.pageBackgroundImage ??
      theme?.wishlistEmailGameImageUrl ??
      theme?.gameLogo ??
      FALLBACK_POSTER,
  };
};

const GALLERY_STYLES = `
  .rm-gallery-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    width: 100%;
    font-family: var(--font-inter, 'Inter', sans-serif);
  }

  .rm-gallery-card-wrapper {
    cursor: pointer;
    transition: transform 0.3s cubic-bezier(.22,.61,.36,1);
  }

  .rm-gallery-card-wrapper:hover {
    transform: scale(1.05);
  }

  .rm-gallery-card-border {
    position: relative;
    width: 100%;
    aspect-ratio: 240 / 150;
    border-radius: var(--radius-xl, 20px);
    padding: 1px;
    background: linear-gradient(135deg, rgba(190,120,255,1), rgba(255,205,110,1), rgba(190,120,255,1));
    background-size: 400% 400%;
    background-position: 0% 50%;
    box-shadow:
      0 0 20px rgba(190,120,255,0.5),
      0 0 40px rgba(114,137,218,0.25);
  }

  .rm-gallery-card {
    width: 100%;
    height: 100%;
    border-radius: calc(var(--radius-xl, 20px) - 1px);
    overflow: hidden;
    background: var(--color-black, #000);
  }

  .rm-gallery-card video,
  .rm-gallery-card img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    display: block;
  }

  .rm-gallery-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: flex-end;
    padding: 10px 14px 12px;
    z-index: 6;
    color: var(--color-white, #fff);
    pointer-events: none;
    border-radius: calc(var(--radius-xl, 20px) - 1px);
    background: linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%);
  }

  .rm-gallery-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.15;
  }

  .rm-gallery-desc {
    font-size: 8px;
    opacity: 0.7;
    line-height: 1.3;
    margin-top: 2px;
  }

  @keyframes rm-gallery-ring-rotate {
    0%   { background-position: 0% 50%; }
    25%  { background-position: 100% 50%; }
    50%  { background-position: 100% 0%; }
    75%  { background-position: 0% 0%; }
    100% { background-position: 0% 50%; }
  }

  .rm-gallery-card-wrapper:hover .rm-gallery-card-border {
    animation: rm-gallery-ring-rotate 1.4s ease-in-out 1;
  }

`;

export const GameGalleryGrid = () => {
  const sectionRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  const { campaigns, loading } = useActiveCampaigns();

  const cards = useMemo<GalleryCard[]>(() => {
    if (!campaigns.length) return [];

    return campaigns
      .map((theme): GalleryCard | null => {
        const normalized = normalizeTheme(theme);
        const slug =
          normalized.defaultSlug ??
          normalized.publicSlug ??
          normalized.urlSlug ??
          normalized.urlSlugs?.[0] ??
          normalized.slug ??
          normalized.campaignId ??
          theme.defaultSlug ??
          theme.publicSlug ??
          theme.urlSlug ??
          theme.urlSlugs?.[0] ??
          theme.slug ??
          theme.campaignId;
        if (!slug) return null;

        const { videoUrl, posterUrl } = resolveThemeVideo(normalized);
        return {
          id: slug,
          title: normalized.gameName ?? normalized.displayName ?? slug,
          description:
            normalized.tagline ??
            normalized.shortDescription ??
            normalized.description ??
            (normalized.studioName ? `By ${normalized.studioName}` : 'Playcast campaign'),
          demoUrl: `${CONFIG.DEMO_BASE_URL}q/${slug}?utm_source=playcast-gallery`,
          videoSrc: videoUrl,
          posterSrc: posterUrl ?? FALLBACK_POSTER,
        };
      })
      .filter((card): card is GalleryCard => Boolean(card));
  }, [campaigns]);

  const openInNewTab = (url: string) => window.open(url, '_blank');

  const hasCards = cards.length > 0;

  return (
    <>
      <style>{GALLERY_STYLES}</style>

      {/* Desktop Layout */}
      <motion.section
        ref={sectionRef}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'visible',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Header */}
          <div style={{ width: '612px', margin: '0 auto 8px', textAlign: 'center' }}>
            <h1 className="text-style-carousel-header" style={{ margin: 0 }}>
              PLAY ALL GAMES{' '}
              <span style={{ color: '#FBBF24' }}>INSTANTLY.</span>
              {' '}ANY PLACE, ANY TIME
            </h1>
          </div>

          {/* Subtitle */}
          <div style={{ width: '300px', margin: '0 auto 32px', textAlign: 'center' }}>
            <p className="text-style-paragraph-white" style={{ margin: 0 }}>
              No download, login, SDK, or install required.
            </p>
          </div>

          {/* Grid */}
          <div style={{ padding: '0 130px' }}>
            {hasCards ? (
            <div className="rm-gallery-grid">
              {cards.map((card, index) => (
                <motion.div
                  key={card.id}
                  className="rm-gallery-card-wrapper"
                  role="button"
                  tabIndex={0}
                  onClick={() => openInNewTab(card.demoUrl)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openInNewTab(card.demoUrl);
                    }
                  }}
                  initial={{ opacity: 0, y: 15 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
                  transition={{ duration: 0.3, delay: index * 0.06, ease: 'easeOut' }}
                >
                  <div className="rm-gallery-card-border">
                    <div className="rm-gallery-card">
                      {card.videoSrc ? (
                        <video key={card.videoSrc} autoPlay muted loop playsInline preload="auto" poster={card.posterSrc}>
                          <source src={card.videoSrc} type="video/mp4" />
                        </video>
                      ) : (
                        <img src={card.posterSrc ?? FALLBACK_POSTER} alt={card.title} />
                      )}
                    </div>
                    <div className="rm-gallery-overlay">
                      <div>
                        <div className="rm-gallery-title">{card.title}</div>
                        <div className="rm-gallery-desc">{card.description}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            ) : (
              <div style={{ width: '100%', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p style={{ margin: 0, opacity: 0.6, fontSize: '18px', fontWeight: 700, color: '#fff', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase' }}>
                  {loading ? 'Active campaigns loading...' : 'No active campaigns at this time.'}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.section>

      {/* Mobile Layout - Single column */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'visible',
          padding: '40px 20px 60px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div style={{ width: '100%', textAlign: 'center', marginBottom: '8px' }}>
          <h1 className="text-style-carousel-header" style={{ margin: 0, fontSize: '28px', lineHeight: '34px' }}>
            PLAY ALL GAMES{' '}
            <span style={{ color: '#FBBF24' }}>INSTANTLY.</span>
            {' '}ANY PLACE, ANY TIME
          </h1>
        </div>

        {/* Subtitle */}
        <div style={{ width: '100%', textAlign: 'center', marginBottom: '24px' }}>
          <p className="text-style-paragraph-white" style={{ margin: 0, fontSize: '12px' }}>
            No download, login, SDK, or install required.
          </p>
        </div>

        {/* Single column grid */}
        {hasCards ? (
        <div style={{ width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {cards.map((card, index) => (
            <motion.div
              key={card.id}
              className="rm-gallery-card-wrapper"
              role="button"
              tabIndex={0}
              onClick={() => openInNewTab(card.demoUrl)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openInNewTab(card.demoUrl);
                }
              }}
              initial={{ opacity: 0, y: 15 }}
              animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
              transition={{ duration: 0.3, delay: index * 0.06, ease: 'easeOut' }}
            >
              <div className="rm-gallery-card-border">
                <div className="rm-gallery-card">
                  {card.videoSrc ? (
                    <video key={card.videoSrc} autoPlay muted loop playsInline preload="auto" poster={card.posterSrc}>
                      <source src={card.videoSrc} type="video/mp4" />
                    </video>
                  ) : (
                    <img src={card.posterSrc ?? FALLBACK_POSTER} alt={card.title} />
                  )}
                </div>
                <div className="rm-gallery-overlay">
                  <div>
                    <div className="rm-gallery-title">{card.title}</div>
                    <div className="rm-gallery-desc">{card.description}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        ) : (
          <div style={{ width: '100%', height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ margin: 0, opacity: 0.6, fontSize: '18px', fontWeight: 700, color: '#fff', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase' }}>
              {loading ? 'Active campaigns loading...' : 'No active campaigns at this time.'}
            </p>
          </div>
        )}
      </motion.section>

    </>
  );
};
